import { useIsAuthenticated } from "@azure/msal-react"

import LinearProgress from "@mui/material/LinearProgress"
import { FC, useEffect, useState } from "react"

import { StandBookingSelection } from "../../account/overview/StandBookingSelection"
import { ShopCartButton } from "../cart/ShopCartButton"
import { SubHeader } from "src/components/services/account/overview/SubHeader"
import { create } from "src/helpers/bem"

import { retrieveCartFromDb } from "src/queriesXRM/cart"
import { fetchCategories } from "src/queriesXRM/categories"
import { fetchProducts } from "src/queriesXRM/products"

import { getPurchaseorderPermissions } from "src/queriesXRM/purchaseorder"
import { loadBookings } from "src/queriesXRM/salesorder"
import {
  mergeBookingDetailPermissions,
  useBookingDetailPermissions,
} from "src/states/bookingDetailPermissions"
import { useBookings } from "src/states/bookings"
import { useLocale } from "src/states/locale"
import { setCart, useCart, useCarts } from "src/states/shop/carts"
import {
  Category,
  mergeCategories,
  useCategories,
} from "src/states/shop/category"

import { mergeFilter, useFilter } from "src/states/shop/filter"
import { mergeProducts, useProducts } from "src/states/shop/products"

import { TranslationMessages } from "src/translations"

import { xRMApiPurchaseorderPermission } from "src/types/xRM"

import styles from "./ShopOverview.module.scss"
import { ShopOverviewNavigation } from "./ShopOverviewNavigation"
import { ShopOverviewProducts } from "./ShopOverviewProducts"
import { ShopOverviewProductsHeader } from "./ShopOverviewProductsHeader"

const bem = create(styles, "ShopOverview")

export type ShopOverviewProps = {
  messages: TranslationMessages
}

export const ShopOverview: FC<ShopOverviewProps> = ({ messages }) => {
  const locale = useLocale()
  const isAuthenticated = useIsAuthenticated()
  const { addUser } = useBookingDetailPermissions()
  const { filterBy } = useFilter()
  const { loading: loadingBookings } = useBookings()
  const { loading: loadingCategories, categories } = useCategories()
  const { loading: loadingProducts } = useProducts()
  const { currentCartId } = useCarts()
  const cart = useCart()
  const carts = useCarts()

  const isWebShopEnabled = process.env.ENABLE_WEBSHOP === "true"

  const loadProducts = async () => {
    mergeProducts({ loading: true })
    const result = await fetchProducts()
    const flatCategoryIds = categories
      .flatMap((c) => c.children ?? [])
      .map((child) => child.id)
    mergeProducts({
      loading: false,
      products: result,
      filteredProducts: result
        .sort(
          (a, b) =>
            flatCategoryIds.indexOf(a.categoryId ?? "") -
            flatCategoryIds.indexOf(b.categoryId ?? ""),
        )
        .filter((p) => p.isValid),
    })
  }

  const loadCategories = async () => {
    mergeCategories({ loading: true })
    mergeCategories({ loading: false, categories: await fetchCategories() })
  }

  useEffect(() => {
    loadProducts()
    loadCategories()
    if (isAuthenticated) {
      loadBookings(locale)
      mergeBookingDetailPermissions({
        open: false,
        addUser: {
          open: false,
          email: addUser.email,
          role: addUser.role,
        },
      })
    }
  }, [locale])

  useEffect(() => {
    if (cart.id !== "0") {
      retrieveCartFromDb(cart.id).then((cartData) => {
        if (cartData) {
          setCart(cartData.products, false, cartData)

          // if (
          //   cartData.revisionNo > cart.revisionNo ||
          //   cartData.revisionNo === -1
          // ) {
          //   console.log("updating cart from db")
          //   setCart(cartData.products, true, cartData)
          // }
        }
      })
    }
  }, [currentCartId])

  useEffect(
    () =>
      mergeFilter((f) => ({
        ...f,
        filterBy: {
          category:
            ([
              ...categories,
              ...categories.flatMap((c) =>
                c.children?.map((subCategory) => subCategory),
              ),
            ].find((c) => c?.id === f.filterBy?.category?.id) as Category) ??
            categories[0],
        },
      })),
    [categories],
  )

  const [loading, setLoading] = useState(false)

  const [standBookingRequests, setStandBookingRequests] = useState<
    xRMApiPurchaseorderPermission[]
  >([])

  useEffect(() => {
    if (isAuthenticated) {
      setLoading(true)
      const fetchStandBookingRequests = async () => {
        setStandBookingRequests(await getPurchaseorderPermissions())
      }
      fetchStandBookingRequests().finally(() => setLoading(false))
    }
  }, [isAuthenticated])

  if (
    (isAuthenticated && loadingBookings) ||
    loadingProducts ||
    loadingCategories
  ) {
    return <LinearProgress color="primary" />
  }

  let headlineCategory = categories[0]?.name ?? ""
  let headlineSubcategory

  if (filterBy?.category?.categoryType === "category") {
    const category = categories?.find((c) => c.id === filterBy?.category?.id)
    headlineCategory = category?.name ?? ""
  }
  if (filterBy?.category?.categoryType === "subcategory") {
    const category = categories?.find(
      (c) => c.id === filterBy?.category?.parentCategoryId,
    )
    const subcategory = category?.children?.find(
      (c) => c.id === filterBy?.category?.id,
    )
    headlineCategory = `${category?.name ?? ""}`
    headlineSubcategory = `${subcategory?.name ?? ""}`
  }

  const renderHeader = () => {
    return (
      <div className={bem("additionals")}>
        <StandBookingSelection isShopPage={true} messages={messages} />
        {isWebShopEnabled && <ShopCartButton />}
      </div>
    )
  }

  return (
    <div className={bem()}>
      <div className={bem("headline__wrapper")}>
        <SubHeader additionals={renderHeader()} />
      </div>
      <div className={bem("wrapper")}>
        <div className={bem("sidebar")}>
          <ShopOverviewNavigation messages={messages} />
          {/* <ShopOverviewInfo messages={messages} />
          <ShopOverviewContact messages={messages} />
          <ShopOverviewLinks messages={messages} /> */}
        </div>
        <div className={bem("products")}>
          <ShopOverviewProductsHeader messages={messages} />
          <ShopOverviewProducts messages={messages} />
        </div>
      </div>
    </div>
  )
}
